import { useEffect, useState } from "react";
import { Ban, ChevronDown, Loader2, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export type SelectableKeywordItem = { id: number; keyword: string };

export interface SelectableProhibitedKeywordListProps {
  /** The words the current search shows, already sorted; the caller filters. */
  items: SelectableKeywordItem[];
  /** The unfiltered count; picks between the two empty-state copies. */
  totalCount: number;
  isLoading: boolean;
  /** `globally` or `for {brand}`. */
  scopeLabel: string;
  layout: "wide" | "narrow";
  /** The single remove in flight; disables that badge's control only. */
  removingId: number | null;
  onRemove: (
    item: SelectableKeywordItem,
    callbacks: { onSuccess: () => void },
  ) => void;
  onRemoveMany: (ids: number[]) => void;
}

export function SelectableProhibitedKeywordList({
  items,
  totalCount,
  isLoading,
  scopeLabel,
  layout,
  removingId,
  onRemove,
  onRemoveMany,
}: SelectableProhibitedKeywordListProps) {
  const [pendingRemove, setPendingRemove] =
    useState<SelectableKeywordItem | null>(null);
  const [multiSelect, setMultiSelect] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // FR-11: keep only ids still shown; written to state so a widened search does not re-tick a word.
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(items.map((i) => i.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleConfirmSingle = () => {
    const item = pendingRemove;
    if (!item) return;
    setPendingRemove(null);
    onRemove(item, {
      onSuccess: () =>
        toast({
          title: "Removed",
          description: `"${item.keyword}" is no longer blocked ${scopeLabel}.`,
        }),
    });
  };

  // Optimistic rows (id < 0) are not on the server yet; they can never be selected.
  const selectableIds = items.filter((i) => i.id >= 0).map((i) => i.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const count = selected.size;

  const toggle = (id: number, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleConfirmBulk = () => {
    const ids = [...selected];
    const n = ids.length;
    if (n === 0) return;
    onRemoveMany(ids);
    setBulkConfirmOpen(false);
    toast({
      title: "Removed",
      description: `${n} ${n === 1 ? "keyword" : "keywords"} removed ${scopeLabel}.`,
    });
    setSelected(new Set());
  };

  const done = () => {
    setSelected(new Set());
    setMultiSelect(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {`Showing ${items.length} ${items.length === 1 ? "keyword" : "keywords"}`}
        </span>
        <div className="flex items-center gap-2">
          {!multiSelect && (
            <Button
              variant="ghost"
              size="sm"
              disabled={items.length === 0}
              onClick={() => setMultiSelect(true)}
            >
              Multi Select
            </Button>
          )}
        </div>
      </div>

      {multiSelect && (
        <BulkActionBar
          count={count}
          onClear={() => setSelected(new Set())}
          entityLabel="keyword"
          forceVisible
          stackRightActions={layout === "narrow"}
          rightActions={
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 text-primary"
                onClick={() =>
                  setSelected(allSelected ? new Set() : new Set(selectableIds))
                }
              >
                {allSelected ? "Deselect all" : "Select all"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 text-primary"
                onClick={done}
              >
                Done
              </Button>
            </>
          }
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5 bg-card"
              >
                Bulk Actions
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={4}>
              <DropdownMenuItem
                disabled={count === 0}
                className="text-destructive focus:text-destructive"
                onSelect={() => setBulkConfirmOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove selected
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BulkActionBar>
      )}

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Ban className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {totalCount === 0
              ? "No prohibited keywords yet. Add one to get started."
              : "No keywords match your search."}
          </p>
        </div>
      ) : multiSelect ? (
        <div
          className={cn(
            "grid gap-x-6 gap-y-1",
            layout === "wide"
              ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2",
          )}
        >
          {items.map((item) => (
            <label
              key={item.id}
              className="flex items-center gap-2 py-1 px-1.5 -mx-1.5 rounded-sm hover:bg-muted/50 cursor-pointer min-w-0"
            >
              <Checkbox
                aria-label={`Select ${item.keyword}`}
                checked={selected.has(item.id)}
                disabled={item.id < 0}
                onCheckedChange={(c) => toggle(item.id, c === true)}
              />
              <TruncatedText className="text-sm">{item.keyword}</TruncatedText>
            </label>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item) => (
            <Badge
              key={item.id}
              className={cn(
                layout === "wide" ? "text-sm" : "text-xs",
                "gap-1 pr-1 bg-status-error-soft text-foreground border-destructive/30 hover:bg-destructive/20",
              )}
            >
              {item.keyword}
              <button
                type="button"
                onClick={() => setPendingRemove(item)}
                disabled={removingId === item.id}
                className="ml-1 rounded-full hover:bg-destructive/20 p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={`Remove ${item.keyword}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title={`Remove "${pendingRemove?.keyword ?? ""}"?`}
        description={`This keyword will no longer be blocked ${scopeLabel}.`}
        confirmLabel="Remove"
        onConfirm={handleConfirmSingle}
      />

      <ConfirmDeleteDialog
        open={bulkConfirmOpen}
        onOpenChange={setBulkConfirmOpen}
        title={`Remove ${count} ${count === 1 ? "keyword" : "keywords"}?`}
        description={`These keywords will no longer be blocked ${scopeLabel}.`}
        confirmLabel="Remove"
        onConfirm={handleConfirmBulk}
      />
    </div>
  );
}
